import { LoginOptionsDto } from '@shared/dto/login-options.dto';
import { Organization } from '@app/interfaces/accounts_list';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { concatMap, map, tap } from 'rxjs/operators';
import { UserModel } from '@models/user.model';
import { Inject, Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { IOperativeUserRole } from '@app/interfaces';
import { NgxHotjarService } from 'ngx-hotjar';
import { APP_CONFIG, AppConfiguration } from '@app/infrastructure/auth/app-configuration';
import { CoralogixRum } from '@coralogix/browser';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private _currentOrganization$: BehaviorSubject<Organization> = new BehaviorSubject(null);
	private _user$: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(null);
	private _loginRequest$: Observable<void>;

	constructor(
		@Inject(APP_CONFIG)
		private appConfig: AppConfiguration,
		private auth0: AuthService,
		private hjService: NgxHotjarService,
	) {}

	/**
	 * Logout user from the application.
	 * Clear localStorage &
	 * redirect to Login page.
	 */
	public logout(): void {
		this._user$.next(null);
		this.clearTableColumnsConfigs();
		this.auth0.logout({ returnTo: window.location.origin });
	}

	public refreshAccessToken(): Observable<string> {
		return this.auth0.getAccessTokenSilently({ ignoreCache: true });
	}

	public requestLogin(params: LoginOptionsDto = null): Observable<void> {
		if (this._loginRequest$) {
			return this._loginRequest$;
		}
		if (!params) {
			params = { step: 'login' };
		}
		this._loginRequest$ = this.auth0.loginWithRedirect({
			...params,
			partnerPrefix: this.appConfig.partner.prefix,
			partnerId: this.appConfig.partner.id,
		});
		return this._loginRequest$;
	}

	public updateUserOrganization(organization: Organization): void {
		this._currentOrganization$.next(organization);
	}

	public get tokenLifetimeSeconds$(): Observable<number> {
		return this.auth0.getIdTokenClaims().pipe(map((token) => token?.exp - token?.iat));
	}

	public get isAuthenticated$(): Observable<boolean> {
		return this.auth0.isAuthenticated$;
	}

	public get organization$(): Observable<Organization> {
		return this._currentOrganization$.asObservable();
	}

	public get organization(): Organization {
		return this._currentOrganization$.getValue();
	}

	public get user$(): Observable<UserModel> {
		return this._user$.pipe(
			concatMap((storedSession: UserModel) => {
				if (!storedSession) {
					return this.auth0.user$.pipe(
						map((user) => {
							if (!user) {
								return null;
							}
							const session = new UserModel(user);
							Sentry.setUser({ name: session.company, segment: session.role });
							CoralogixRum.setUserContext({
								user_id: session.id,
								user_name: '',
								user_email: '',
								user_metadata: { company: session.company, org_id: session.organizationId, role: session.role },
							});
							window.zestyHost.userRole = session.role;
							window.zestyHost.customerType = this.appConfig.customerType;
							this._user$.next(session);
							return session;
						}),
						tap((session) => {
							if (this.appConfig.hotJar && session) {
								const isZestyUser =
									session.email?.toLowerCase().includes('zesty') || session.email?.toLowerCase().includes('cloudvisor');
								this.hjService.hj('identify', session.id, {
									company: session.company,
									whiteLabel: session.whiteLabel,
									userEmailOrigin: isZestyUser ? 'zesty' : 'other',
								});
							}
						}),
					);
				}
				return of(storedSession);
			}),
		);
	}

	public get userRole(): string {
		this.validateSessionPresence();
		return this._user$.value.role;
	}

	public get userEmail(): string {
		this.validateSessionPresence();
		return this._user$.value.email;
	}

	public get userName(): string {
		return this._user$.value?.userName;
	}

	public get isUserEmailVerified(): boolean {
		this.validateSessionPresence();
		return this._user$.value.isEmailVerified;
	}

	public get userProfile(): string {
		this.validateSessionPresence();
		return this._user$.value.profile;
	}

	public get userId(): string {
		this.validateSessionPresence();
		return this._user$.value.id;
	}

	public get userWhiteLabel(): string {
		this.validateSessionPresence();
		return this._user$.value.whiteLabel;
	}

	public get userNickname(): string {
		this.validateSessionPresence();
		return this._user$.value.nickname;
	}

	public get userCompany(): string {
		this.validateSessionPresence();
		return this._user$.value.company;
	}

	public get userResellerId(): string {
		this.validateSessionPresence();
		return this._user$.value.resellerId;
	}

	public get organizationId(): string {
		this.validateSessionPresence();
		return this._user$.value.organizationId;
	}

	public get organizationApiKey(): string {
		this.validateOrganizationPresence();
		return this._currentOrganization$.value.api_key;
	}

	public set organizationApiKey(value: string) {
		this.validateOrganizationPresence();
		this._currentOrganization$.value.api_key = value;
	}

	// @Todo: remove usage
	public get productLabel(): string {
		return this.appConfig.partner.prefix;
	}

	private validateSessionPresence(): void {
		if (!this._user$.value) {
			throw new Error('User session is not fetched yet.');
		}
	}

	private validateOrganizationPresence(): void {
		if (!this._currentOrganization$.value) {
			throw new Error('Organization is not fetched yet.');
		}
	}

	/**
	 * Find table config keys in lc and remove them
	 */
	private clearTableColumnsConfigs(): void {
		Object.keys(localStorage)
			?.filter((key) => key.includes('COLUMNS_CONFIG'))
			?.forEach((key) => localStorage.removeItem(key));
	}

	public isZestyEmployee(): boolean {
		const permittedUserRoles: string[] = [
			IOperativeUserRole.admin,
			IOperativeUserRole.salesManager,
			IOperativeUserRole.csm,
			IOperativeUserRole.partner,
			IOperativeUserRole.riBuyers,
		];

		return permittedUserRoles.includes(this.userRole);
	}
}
